<script setup lang="ts">
import { computed } from 'vue';
import { useTranslation } from 'i18next-vue';
import Button from '@/components/buttons/Button.vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import { Clause, isComplete, operatorLabels, operatorsFor, ParamInfo } from './condition';
import NumberField from '@/components/forms/NumberField/NumberField.vue';

const props = defineProps<{
  clause: Clause;
  paramItems: { title: string; value: string }[];
  paramInfo?: ParamInfo;
  valueTitle?: string;
  error?: string;
  applicable: boolean;
}>();

const emits = defineEmits<{
  'update:clause': [Clause];
  remove: [];
}>();

const { t } = useTranslation();

// Keeps a removed param selectable under its cached label.
const paramSelectItems = computed(() => {
  const param = props.clause.param;
  const label = props.paramInfo?.label;
  if (!param || !label || props.paramItems.some((item) => item.value === param)) {
    return props.paramItems;
  }

  return [...props.paramItems, { title: label, value: param }];
});

const numberInfo = computed(() =>
  props.paramInfo?.type === 'number' ? props.paramInfo : undefined,
);

const operatorItems = computed(() =>
  operatorsFor(props.paramInfo?.type).map((operator) => ({
    title: operatorLabels[operator],
    value: operator,
  })),
);

// A stale value stays selectable under its former title, so the select shows what the condition says.
const enumItems = computed(() => {
  const info = props.paramInfo;
  const { value } = props.clause;
  if (info?.type !== 'enum') return [];
  if (value === undefined || info.options.some((option) => option.value === value)) {
    return info.options;
  }

  return [...info.options, { title: props.valueTitle ?? String(value), value }];
});

// v-select turns an undefined model into its `null` default, which selects the "not set" item;
// an empty array selects nothing.
const enumModelValue = computed(() => (props.clause.value === undefined ? [] : props.clause.value));

function updateEnumValue(value: string | number | null | unknown[]) {
  // Unlike updateValue, keeps null: for the enum "not set" option it is a real selection.
  emits('update:clause', { ...props.clause, value: value as string | number | null });
}

// A clause still being filled in stays quiet until edited (rule); a stale value shows on load.
const paramError = computed(() => (props.applicable ? undefined : props.error));
const valueError = computed(() =>
  props.applicable && isComplete(props.clause) ? props.error : undefined,
);

const requiredRule = () => isComplete(props.clause) || t('Validation.PropertyRequired');

function selectParam(param: string | undefined) {
  emits('update:clause', { param, operator: '===', value: undefined });
}

function selectOperator(operator: Clause['operator']) {
  emits('update:clause', { ...props.clause, operator });
}

function updateValue(value: number | string | undefined | null) {
  emits('update:clause', { ...props.clause, value: value ?? undefined });
}
</script>

<template>
  <div class="d-flex align-start ga-2">
    <v-select
      :modelValue="clause.param"
      :items="paramSelectItems"
      :errorMessages="paramError"
      class="clause-input flex-1-1-0"
      @update:modelValue="selectParam"
    />

    <div class="clause-action d-flex align-center">
      <v-menu>
        <template #activator="{ props: activatorProps }">
          <Button
            size="small"
            variant="secondary"
            class="px-2"
            :disabled="!clause.param"
            :aria-label="t('Operator')"
            :label="operatorLabels[clause.operator]"
            v-bind="activatorProps"
          />
        </template>

        <v-list>
          <v-list-item
            v-for="operator in operatorItems"
            :key="operator.value"
            :active="operator.value === clause.operator"
            @click="selectOperator(operator.value)"
          >
            {{ operator.title }}
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <NumberField
      v-if="numberInfo"
      :modelValue="typeof clause.value === 'number' ? clause.value : undefined"
      :format="numberInfo.format"
      :suffix="numberInfo.format.unitAbbreviation ?? undefined"
      :rules="[requiredRule]"
      :errorMessage="valueError"
      class="clause-input flex-1-1-0"
      @update:modelValue="updateValue"
    />

    <v-text-field
      v-else-if="paramInfo?.type === 'string'"
      :modelValue="typeof clause.value === 'string' ? clause.value : undefined"
      :rules="[requiredRule]"
      :errorMessages="valueError"
      class="clause-input flex-1-1-0"
      @update:modelValue="updateValue"
    />

    <v-select
      v-else-if="paramInfo?.type === 'enum'"
      :modelValue="enumModelValue"
      :items="enumItems"
      :rules="[requiredRule]"
      :errorMessages="valueError"
      class="clause-input flex-1-1-0"
      @update:modelValue="updateEnumValue"
    />

    <v-text-field v-else disabled class="clause-input flex-1-1-0" />

    <div class="clause-action d-flex align-center">
      <PtwDeleteButton @delete="emits('remove')" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/styles/variables';

.clause-input {
  min-width: 0;
}

.clause-action {
  height: variables.$input-control-height;
}
</style>
